/* Libraries */
import moment from "moment-timezone";
import _ from "lodash";
import emitter from "@/emitter";

/* Enums */
import ChainTypes from "@/enums/ChainTypes";
import EditCodes from "@/enums/EditCodes";
import EventStatus from "@/enums/EventStatus";
import EventTypes from "@/enums/EventTypes";
import PlaylistTypes from "@/enums/PlaylistTypes";
import SequencerModes from "@/enums/SequencerModes";
import SkipTypes from "@/enums/SkipTypes";
import ExecuteCommandTypes from "@/enums/ExecuteCommandTypes";

const EventPropertiesMixin = {

	data() {
		return {
			spotBlockIcon: "mdi-currency-usd",
			localBreakIcon: "mdi-map-marker-radius",
			iconETM: "mdi-clock",
			iconModeSelection: "mdi-swap-horizontal",
			iconStartLiveShow: "mdi-map-marker-star",
			iconEndLiveShow: "mdi-flag-checkered",
			songDetailsPopup: {
				open: false
			},
			contextMenu: {
				open: false,
				x: 0,
				y: 0
			},
			skipSpotReasonMenu: {
				open: false,
				x: 0,
				y: 0
			},
			skipTypes: [
				{description: "Client Request", value: SkipTypes.ClientRequest},
				{description: "Emergency", value: SkipTypes.Emergency},
				{description: "Incorrect Copy", value: SkipTypes.IncorrectCopy},
				{description: "Event Change", value: SkipTypes.EventChange},
				{description: "Recent News Event", value: SkipTypes.RecentNewsEvent},
				{description: "Other", value: SkipTypes.Other}
			],
			skipSpotForm: {
				valid: true,
				skipType: null,
				otherReasonComment: null,
				loading: false
			},
			stopOrPauseClicked: false,
			chainTypes: [
				{description: "Segue", icon: "Segue", value: 1},
				{description: "Auto Post", icon: "AutoPost", value: 2},
				{description: "Stop", icon: "Stop", value: 3},
				{description: "Link Song", icon: "LinkSong", value: 4}
			],
			chainTypeUpdating: false,
			modeUpdating: false,
			skipSpotBlockConfirmationDialog: {
				open: false,
				message: "Are you sure you want to delete this Spot Block from the playlist?"
			},
		}
	},

	computed: {
		sequencer() {
			return this.$store.getters.getSequencer(this.selectedStation.StationID);
		},

		sequencerEvent() {
			return this.$store.getters.getSequencerItemByEventID(
				this.selectedStation.StationID,
				this.event.EventID
			);
		},

		supportedChainTypes() {
			const assetTypeIDSupportedChainTypes = this.$store.getters.getAssetTypes()
			                                           .find(at => at.AssetTypeID === this.asset?.AssetTypeID)
				?.SupportedChainTypes;

			return _.isEmpty(assetTypeIDSupportedChainTypes) ?
				   this.chainTypes :
			       this.chainTypes.filter(ct => assetTypeIDSupportedChainTypes?.includes(ct.value));
		},

		chainTypeIcon() {
			const icon = this.chainTypes.find(type => type.value === this.chainType)?.icon;
			return icon ? `${icon}_silver.svg` : "";
		},

		eventDetails() {
			return {
				EventID: {
					value: this.event.EventID,
					copyButton: true
				},
				PlaylistID: {
					value: this.event.PlaylistID,
					copyButton: true
				},
				EventType: EventTypes.getName(this.event.EventType),
				SequenceNumber: this.event.SequenceNumber || "-",
				Created: moment(this.event.CreatedDateTime).format("YYYY-MM-DD, HH:mm") || "-",
				Modified: moment(this.event.ModifiedDateTime).format("YYYY-MM-DD, HH:mm") || "-",
				Origin: this.event.Origin || "-"
			}
		},

		isPlayAsset() {
			return this.event.EventType === EventTypes.PlayAsset;
		},

		isSpotBlock() {
			return this.event.EventType === EventTypes.SpotBlock;
		},

		isDVTBlock() {
			return this.event.EventType === EventTypes.DVTBlock;
		},

		isRejoin() {
			return this.event.EventType === EventTypes.Rejoin;
		},

		isLocalSpotBlock() {
			return this.isSpotBlock && this.event.ChildPlaylist?.PlaylistType === PlaylistTypes.SpotBlock;
		},

		isNetworkSpotBlock() {
			return this.isSpotBlock && this.event.ChildPlaylist?.PlaylistType === PlaylistTypes.NetworkSpotBlock;
		},

		isComment() {
			return this.event.EventType === EventTypes.Comment;
		},

		isETM() {
			return this.event.EventType === EventTypes.ETM;
		},

		isLocalBreak() {
			return this.event.EventType === EventTypes.LocalBreak;
		},

		isMusicFillPos() {
			return this.event.EventType === EventTypes.MusicFillPos;
		},

		isLiveRead() {
			return this.event.LinearAdData?.liveRead;
		},

		isVoiceTrack() {
			return this.isPlayAsset && this.$store.getters.getAssetTypeById(this.event.AssetTypeID)?.Recordable;
		},

		isModeSelectionEvent() {
			return this.event.EventType === EventTypes.ExecuteCommand &&
			       this.event.ExecuteCommand.ExecuteCommandType === ExecuteCommandTypes.ModeChange;
		},

		isStartLiveShow() {
			return this.event.EventType === EventTypes.ExecuteCommand &&
			       this.event.ExecuteCommand.ExecuteCommandType === ExecuteCommandTypes.SyndicationOriginationStart;
		},

		isEndLiveShow() {
			return this.event.EventType === EventTypes.ExecuteCommand &&
			       this.event.ExecuteCommand.ExecuteCommandType === ExecuteCommandTypes.SyndicationOriginationEnd;
		},

		statusCode() {
			return this.event?.StatusCode;
		},

		isReady() {
			return this.statusCode === EventStatus.Ready;
		},

		isNowPlaying() {
			return this.statusCode === EventStatus.Current;
		},

		isPaused() {
			return this.sequencer ?
			       this.sequencerEvent?.StatusCode === EventStatus.Paused : this.statusCode === EventStatus.Paused;
		},

		isFutureEvent() {
			const playlist = this.$store.getters.getPlaylistById(
				{stationID: this.selectedStation.StationID, playlistID: this.event.PlaylistID}
			);
			return this.isReady ||
			       (this.event.empty && this.event.isFuture) ||
			       (playlist?.HotlineSequence < this.event.SequenceNumber && !this.isPlayed);
		},

		isNextEvent() {
			return this.$store.getters.getSequencerItems(
				this.selectedStation.StationID
			)[1]?.EventID === this.event.EventID;
		},

		isPlayed() {
			return [EventStatus.Played, EventStatus.PendingPlayed].includes(this.statusCode);
		},

		progressCountdownEnabled() {
			return this.$store.getters.userPreferences("nowPlayingEventProgressCountdownEnabled") ?? true;
		},

		isSequencerModeAuto() {
			return this.$store.getters.getSequencerMode(this.selectedStation.StationID) === SequencerModes.Auto;
		},

		isSequencerModeLiveAssist() {
			return this.$store.getters.getSequencerMode(this.selectedStation.StationID) === SequencerModes.LiveAssist;
		},

		//sequencerActionStatus: used in watcher
		sequencerActionStatus() {
			if (this.$store.getters.liveMode()) {
				return this.$store.getters.sequencerActionPending(this.selectedStation.StationID) &&
					   this.$store.getters.getSequencerSnapshotUpdated(this.selectedStation.StationID);
			}
			return {pending: false};
		},

		chainType() {
			if (this.event?.Chain) {
				return this.event.Chain;
			}
			else {
				return ChainTypes.Segue;
			}
		},

		chainTypeIsStop() {
			return this.chainType === ChainTypes.Stop && this.isSequencerModeLiveAssist;
		},

		mode() {
			return this.event.ExecuteCommand?.ModeChange?.NewMode;
		},

		errorCode() {
			return this.event?.ErrorCode;
		},

		darkMode() {
			return this.$store.getters.darkMode();
		},

		spotBlockColor() {
			return !this.darkMode ? "rgb(2, 140, 60)" : "rgb(49, 234, 65)";
		},

		colorETM() {
			return !this.darkMode ? "rgb(60, 60, 60)" : "rgb(255, 255, 255)";
		},

		commentColor() {
			return "rgb(255, 255, 255)";
		},

		liveReadColor() {
			return !this.darkMode ? "rgb(226, 33, 0)" : "rgb(255, 81, 60)";
		},

		localBreakColor() {
			return "rgb(63, 81, 166)";
		},

		modeSelectionEventColor() {
			return "rgb(98, 0, 234)";
		},

		DVTBlockEventColor() {
			return !this.darkMode ? "rgb(10,11,9)" : "rgb(252,131,62)";
		}
	},

	methods: {
		openContextMenu(event) {
			this.contextMenu.x = event.clientX;
			this.contextMenu.y = event.clientY;

			this.contextMenu.open = !this.contextMenu.open;
		},

		closeContextMenu(ev) {
			if (!this.$el.contains(ev.target)) {
				this.contextMenu.open = false;
			}
		},

		openAssetManagement() {
			this.$store.dispatch("getAsset", this.event.Asset.AssetID)
			    .then(asset => {
					const unreleasedAsset = moment(asset.ReleaseDate).isAfter(moment());
					const accessUnreleasedAsset = this.$store.getters.isFeatureEnabledByAssetTypeID({
						object_id: this.selectedStation.StationID,
						reference: "zettacloud_asset_access_unreleased_type",
						asset_type_id: asset.AssetTypeID
					});

					if (!unreleasedAsset || accessUnreleasedAsset) {
						this.$store.commit("setAssetManagementSelectedStationMarkers", {
							stationID: "Global",
							stationName: "Global",
							markers: asset?.POIMarkup,
						});
						this.$store.commit("setAssetManagementStationID", this.selectedStation.StationID);
						this.$store.commit("setAssetManagementAsset", asset);
						this.$store.commit("setAssetManagementEvent", this.event);
						this.$store.commit("setAssetManagementIsOpen", true);
					}
					else {
						this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {
							message: "You don't have access to access unreleased assets!"
						});
					}
				})
		},

		toggleProgressCountdown() {
			this.$store.dispatch("toggleNowPlayingEventProgressCountdown", !this.progressCountdownEnabled);
		},

		openSkipSpotReasonMenu(ev) {
			this.skipSpotReasonMenu.x = ev.clientX;
			this.skipSpotReasonMenu.y = ev.clientY;
			this.skipSpotReasonMenu.open = true;
		},

		closeSkipSpotReasonMenu() {
			this.skipSpotReasonMenu.open = false;
		},

		skipEvent({skip, spot = false}, callback) {
			let data = {
				EventID: this.event.EventID,
				Skipped: skip,
				EditComment: spot && this.skipSpotForm.otherReasonComment ?
				             this.skipSpotForm.otherReasonComment :
				             this.$store.getters.getUserFullName(), //username that skipped the event.
			};

			if (spot) {
				this.skipSpotForm.loading = true;
				data.SkipType = this.skipSpotForm.skipType;
			}
			else {
				data.EditCode = EditCodes.ManualSkip;
			}

			const successCallback = () => {
				if (data.Skipped) {
					const icon = this.isPlayAsset ? this.assetTypeIcon :
					             this.isETM ? this.iconETM : null;
					const color = this.isPlayAsset ? this.assetType?.Color : "#ffffff";
					const title = this.isPlayAsset ?
					              (this.event.Asset?.Title ?
					               `<span class="font-weight-bold">${this.event.Asset?.Title}</span>` : "The event") :
					              this.isLiveRead ? "Live Read" : EventTypes.getName(this.event.EventType);
					const message = `<v-icon class="mdi ${icon} mr-1" style="color: ${color}"></v-icon>${title} was ejected.`

					this.$store.commit(
						"SHOW_NOTIFICATION_WITH_ACTION", {
							message,
							action: {
								text: "Undo",
								click: (callback) => {
									this.skipEvent({skip: false}, callback);
								}
							}, timeout: 15000
						}
					);
				}

				/* Update all the spot block's children events with the new Skipped value */
				if (this.isSpotBlock) {
					this.event.ChildPlaylist?.Events?.forEach(spot => {
						const spotData = {
							EventID: spot.EventID,
							Skipped: !this.event.Skipped,
							EditComment: this.skipSpotForm.otherReasonComment,
							SkipType: this.skipSpotForm.skipType
						};

						this.updateEventSkipped(spotData);
					});
				}

				// Callback from notification Undo action
				if (callback) {
					callback();
				}
			};

			const finallyCallback = () => {
				if (this.isSpotBlock || spot) {
					this.skipSpotForm.loading = false;
					this.skipSpotReasonMenu.open = false;
				}
			};

			this.updateEventSkipped(data, successCallback, finallyCallback);
		},

		updateEventSkipped(payload, successCallback, finallyCallback) {
			this.$store.dispatch("updatePlaylistEvent", payload).then((response) => {
				if (response.status === 200) {
					if (successCallback) {
						successCallback();
					}
				}
				else {
					this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {
						message: `An error occurred when trying to ${!payload.Skipped ? "un" : ""}skip the event.`
					});
				}
			}).finally(() => {
				if (finallyCallback) {
					finallyCallback();
				}
			});
		},

		updateChainType(chainType) {
			if (chainType !== this.chainType) {
				this.chainTypeUpdating = true;
				this.$store.dispatch("updatePlaylistEvent", {
					EventID: this.event.EventID,
					Chain: chainType
				});
			}
		}
	},

	watch: {
		statusCode() {
			this.setColors();
		},

		assetType() {
			this.setColors();
		},

		darkMode() {
			this.setColors();
		},

		sequencerActionStatus(status) {
			this.stopOrPauseClicked = status.pending;
		},

		chainType() {
			this.chainTypeUpdating = false;
		},

		mode() {
			this.modeUpdating = false;
		}
	},

	mounted() {
		this.setColors();
	},

	created() {
		// Close other open context menus on right click
		window.addEventListener("contextmenu", this.closeContextMenu);
	},

	beforeDestroy() {
		window.removeEventListener("contextmenu", this.closeContextMenu);
	},

	unmounted() {
		// in case of skipping an event from the context menu we should re-enable the playlist scrolling
		if (this.event.Skipped) {
			emitter.emit("toggle-playlist-context-menu");
		}
	}
}

export default EventPropertiesMixin;
